import * as XLSX from 'sheetjs-style';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Export data to PDF
const exportToPdf = async (tableId, reportInfo) => {
  const input = document.getElementById(tableId);

  // Measure the width of the table
  const tableWidth = input.offsetWidth; // Get the rendered table width in pixels
  const pdfWidth = tableWidth * 0.264583; // Convert to mm

  const canvas = await html2canvas(input, { scale: 1, useCORS: true });
  const imgData = canvas.toDataURL('image/png', 0.5);

  let pdfHeight;
  if (reportInfo.type === "Installed Capacity" || reportInfo.type === "Net Calorific") {
    pdfHeight = canvas.height * 0.264583;
  } else {
    pdfHeight = 148; // Fixed height
  }

  const pdf = new jsPDF('l', 'mm', [pdfWidth, pdfHeight]);

  // Add report info at the top
  pdf.setFontSize(12);
  pdf.setFont("helvetica", "bold");
  pdf.text(`Report Period Year: ${reportInfo.year}`, 10, 20);
  pdf.text(`Report Quarter: ${reportInfo.quarter}`, 10, 30);
  pdf.text(`Agency: ${reportInfo.agency}`, 10, 40);
  pdf.text(`Data Provider: ${reportInfo.dataProvider}`, 10, 50);
  pdf.text(`Report Status: ${reportInfo.status}`, 10, 60);
  pdf.text(`Report Type: ${reportInfo.type}`, 10, 70);
  pdf.line(10, 75, pdfWidth - 10, 75);

  const imgWidth = canvas.width * 0.264583;
  const imgHeight = canvas.height * 0.264583;
  const scaleFactor = 0.7;
  const scaledWidth = imgWidth * scaleFactor;
  const scaledHeight = imgHeight * scaleFactor;

  pdf.addImage(imgData, 'PNG', 0, 80, scaledWidth, scaledHeight);
  pdf.save(`${reportInfo.type}-${reportInfo.agency} (${reportInfo.year}-${reportInfo.quarter}).pdf`);
};

// Export data to Excel with formatting
const exportToExcel = (tableId, reportInfo) => {
  const table = document.getElementById(tableId);

  // Extract table rows and replace input/select elements with their displayed values
  const rows = Array.from(table.rows).map(row =>
    Array.from(row.cells).map(cell => {
      const input = cell.querySelector('input');
      const select = cell.querySelector('select');
      if (input) return input.value.trim();
      if (select) return select.options[select.selectedIndex]?.text || '';
      return cell.textContent.trim();
    })
  );

  // Information rows (shown before the table, like in the PDF)
  const infoRows = [
    [`Report Period Year:`, reportInfo.year],
    [`Report Quarter:`, reportInfo.quarter],
    [`Agency:`, reportInfo.agency],
    [`Data Provider:`, reportInfo.dataProvider],
    [`Report Status:`, reportInfo.status],
    [`Report Type:`, reportInfo.type],
    [], // Empty row for spacing before the table
  ];

  // Combine info rows and actual table data
  const combinedData = [...infoRows, ...rows];

  // Create worksheet from the combined data
  const worksheet = XLSX.utils.aoa_to_sheet(combinedData);

  // Auto-adjust column widths based on the longest cell content in each column
  const colCount = combinedData.reduce((max, row) => Math.max(max, row.length), 0);
  worksheet['!cols'] = Array.from({ length: colCount }).map((_, colIdx) => {
    const maxLength = combinedData.reduce((max, row) => {
      const val = row[colIdx] ? row[colIdx].toString() : '';
      return Math.max(max, val.length);
    }, 10); // minimum width fallback
    return { wch: maxLength + 2 };
  });

  // Style the header row (bold font, background color, borders)
  const headerRowIndex = infoRows.length;
  combinedData[headerRowIndex]?.forEach((_, colIdx) => {
    const cellRef = XLSX.utils.encode_cell({ r: headerRowIndex, c: colIdx });
    if (worksheet[cellRef]) {
      worksheet[cellRef].s = {
        fill: { fgColor: { rgb: 'DCE6F1' } }, // light blue
        font: { bold: true },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: '000000' } },
          bottom: { style: 'thin', color: { rgb: '000000' } },
          left: { style: 'thin', color: { rgb: '000000' } },
          right: { style: 'thin', color: { rgb: '000000' } }
        }
      };
    }
  });

  // Create a new workbook and append the styled worksheet
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Report Data');

  // Generate filename and export the workbook
  const fileName = `${reportInfo.type}-${reportInfo.agency} (${reportInfo.year}-${reportInfo.quarter}).xlsx`;
  XLSX.writeFile(workbook, fileName);
};

export { exportToPdf, exportToExcel }; // Named export (or use default export if preferred)